/**
 * @module components/Search/SearchDropdown
 * @description Dropdown de resultados de búsqueda global en tiempo real.
 *
 * Filtra y agrupa resultados en categorías: Controles ISO, Tareas, Evidencias
 * y Métricas. Se activa desde el campo de búsqueda del `Header` cuando el
 * usuario escribe al menos 2 caracteres.
 *
 * @see Header que monta este componente y gestiona `searchQuery`
 */
import React from 'react';
import { FileText, CheckSquare, Upload, TrendingUp } from 'lucide-react';
import type { ISOControl, Task, Evidence } from '../../types/iso';

interface SearchDropdownProps {
  searchQuery: string;
  controls: ISOControl[];
  onClose: () => void;
}

export function SearchDropdown({ searchQuery, controls, onClose }: SearchDropdownProps) {
  // Filter controls by search query
  const matchingControls = React.useMemo(() => {
    if (!searchQuery || searchQuery.length < 2 || !controls || controls.length === 0) return [];

    const query = searchQuery.toLowerCase();
    return controls.filter(control => {
      if (!control) return false;
      return (
        control.code?.toLowerCase()?.includes(query) ||
        control.title?.toLowerCase()?.includes(query) ||
        control.description?.toLowerCase()?.includes(query) ||
        control.category?.toLowerCase()?.includes(query)
      );
    }).slice(0, 5); // Limit to 5 results
  }, [searchQuery, controls]);

  // Extract tasks from matching controls
  const matchingTasks = React.useMemo(() => {
    if (!searchQuery || searchQuery.length < 2 || !controls || controls.length === 0) return [];

    const query = searchQuery.toLowerCase();
    const allTasks: Array<Task & { controlCode: string; controlTitle: string }> = [];

    controls.forEach(control => {
      if (!control) return;
      const tasks = control.tasks || [];
      tasks.forEach(task => {
        if (!task) return;
        if (
          task.title?.toLowerCase()?.includes(query) ||
          task.description?.toLowerCase()?.includes(query)
        ) {
          allTasks.push({
            ...task,
            controlCode: control.code || '',
            controlTitle: control.title || ''
          });
        }
      });
    });

    return allTasks.slice(0, 3); // Limit to 3 results
  }, [searchQuery, controls]);

  // Extract evidence from matching controls
  const matchingEvidence = React.useMemo(() => {
    if (!searchQuery || searchQuery.length < 2 || !controls || controls.length === 0) return [];

    const query = searchQuery.toLowerCase();
    const allEvidence: Array<Evidence & { controlCode: string; controlTitle: string }> = [];

    controls.forEach(control => {
      if (!control) return;
      const evidence = control.evidence || [];
      evidence.forEach(ev => {
        if (!ev) return;
        if (
          ev.title?.toLowerCase()?.includes(query) ||
          ev.description?.toLowerCase()?.includes(query) ||
          ev.filename?.toLowerCase()?.includes(query)
        ) {
          allEvidence.push({
            ...ev,
            controlCode: control.code || '',
            controlTitle: control.title || ''
          });
        }
      });
    });

    return allEvidence.slice(0, 3); // Limit to 3 results
  }, [searchQuery, controls]);

  const totalResults = matchingControls.length + matchingTasks.length + matchingEvidence.length;

  if (!searchQuery || searchQuery.length < 2) {
    return (
      <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 z-50">
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
          Escribe al menos 2 caracteres para buscar...
        </p>
      </div>
    );
  }

  if (totalResults === 0) {
    return (
      <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 z-50">
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
          No se encontraron resultados para "{searchQuery}"
        </p>
      </div>
    );
  }

  return (
    <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-96 overflow-y-auto z-50">
      {/* Header with result count */}
      <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-2">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {totalResults} resultado{totalResults !== 1 ? 's' : ''} encontrado{totalResults !== 1 ? 's' : ''}
          </p>
          <button
            onClick={onClose}
            className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            Cerrar
          </button>
        </div>
      </div>

      {/* Controls section */}
      {matchingControls.length > 0 && (
        <div className="px-2 py-2">
          <div className="px-2 py-1 flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
              Controles ({matchingControls.length})
            </h3>
          </div>
          <div className="space-y-1">
            {matchingControls.map(control => (
              <button
                key={control.id}
                onClick={() => {
                  // TODO: Navigate to control detail
                  onClose();
                }}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                        {control.code}
                      </span>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        control.status === 'completed'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : control.status === 'in_progress'
                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400'
                      }`}>
                        {control.status === 'completed' ? 'Completado' :
                         control.status === 'in_progress' ? 'En progreso' : 'Pendiente'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-900 dark:text-white mt-0.5 truncate">
                      {control.title}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {control.category}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Tasks section */}
      {matchingTasks.length > 0 && (
        <div className="px-2 py-2 border-t border-gray-200 dark:border-gray-700">
          <div className="px-2 py-1 flex items-center gap-2">
            <CheckSquare className="w-4 h-4 text-green-600 dark:text-green-400" />
            <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
              Tareas ({matchingTasks.length})
            </h3>
          </div>
          <div className="space-y-1">
            {matchingTasks.map(task => (
              <button
                key={task.id}
                onClick={() => {
                  // TODO: Navigate to task/control
                  onClose();
                }}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 dark:text-white truncate">
                      {task.title}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      Control: {task.controlCode} - {task.controlTitle}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        task.status === 'completed'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : task.status === 'in_progress'
                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400'
                      }`}>
                        {task.status === 'completed' ? 'Completada' :
                         task.status === 'in_progress' ? 'En progreso' : 'Pendiente'}
                      </span>
                      {task.dueDate && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Vence: {new Date(task.dueDate).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Evidence section */}
      {matchingEvidence.length > 0 && (
        <div className="px-2 py-2 border-t border-gray-200 dark:border-gray-700">
          <div className="px-2 py-1 flex items-center gap-2">
            <Upload className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
              Evidencias ({matchingEvidence.length})
            </h3>
          </div>
          <div className="space-y-1">
            {matchingEvidence.map(ev => (
              <button
                key={ev.id}
                onClick={() => {
                  // TODO: Open evidence or navigate to control
                  onClose();
                }}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 dark:text-white truncate">
                      {ev.title}
                    </p>
                    {ev.filename && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        📎 {ev.filename}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      Control: {ev.controlCode}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Footer with tips */}
      <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-2 bg-gray-50 dark:bg-gray-900/50">
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
          💡 Usa las flechas ↑↓ para navegar • Enter para seleccionar • Esc para cerrar
        </p>
      </div>
    </div>
  );
}
